import {
  INSUFFICIENT_INFORMATION_PLACEHOLDER,
  manualInstructionWithGuidance,
  type PackagingMinimisationAssessmentSectionRecord,
} from '@artem-lex/compliance-documents'

export type PackagingMinimisationCriteriaAgentResult = {
  productProtection: PackagingMinimisationStandardCriterionAgentShape
  packagingManufacturingProcess: PackagingMinimisationStandardCriterionAgentShape
  packingFillingProcess: PackagingMinimisationStandardCriterionAgentShape
  logistics: PackagingMinimisationStandardCriterionAgentShape
  productPresentationAndMarketing: PackagingMinimisationStandardCriterionAgentShape
  userConsumerAcceptance: PackagingMinimisationStandardCriterionAgentShape
  informationPC: PackagingMinimisationStandardCriterionAgentShape
  safetyPC: PackagingMinimisationStandardCriterionAgentShape
  legislationPC: PackagingMinimisationLegislationCriterionAgentShape
}

export type PackagingMinimisationStandardCriterionAgentShape = {
  isCriticalArea: boolean
  explanation: string
  followUpQuestion?: string | null
  mostImportantRequirement?: string | null
}

export type PackagingMinimisationLegislationCriterionAgentShape = {
  explanation: string
}

export type PackagingMinimisationOutcomeAgentShape = {
  totalMinimumNecessaryWeightGrams: number | null
  totalMinimumNecessaryVolumeCm3: number | null
  reasoning?: string
}

type StandardCriterionKey =
  | 'productProtection'
  | 'packagingManufacturingProcess'
  | 'packingFillingProcess'
  | 'logistics'
  | 'productPresentationAndMarketing'
  | 'userConsumerAcceptance'
  | 'informationPC'
  | 'safetyPC'

type CriterionSectionEntry =
  | {
      sectionId: string
      criterionKey: StandardCriterionKey
      kind: 'standard'
    }
  | {
      sectionId: string
      criterionKey: 'legislationPC'
      kind: 'legislation'
    }

const PACKAGING_MINIMISATION_CRITERION_SECTIONS = [
  { sectionId: '2.3.1', criterionKey: 'productProtection', kind: 'standard' },
  {
    sectionId: '2.3.2',
    criterionKey: 'packagingManufacturingProcess',
    kind: 'standard',
  },
  {
    sectionId: '2.3.3',
    criterionKey: 'packingFillingProcess',
    kind: 'standard',
  },
  { sectionId: '2.3.4', criterionKey: 'logistics', kind: 'standard' },
  {
    sectionId: '2.3.5',
    criterionKey: 'productPresentationAndMarketing',
    kind: 'standard',
  },
  {
    sectionId: '2.3.6',
    criterionKey: 'userConsumerAcceptance',
    kind: 'standard',
  },
  { sectionId: '2.3.7', criterionKey: 'informationPC', kind: 'standard' },
  { sectionId: '2.3.8', criterionKey: 'safetyPC', kind: 'standard' },
  { sectionId: '2.3.9', criterionKey: 'legislationPC', kind: 'legislation' },
] as const satisfies ReadonlyArray<CriterionSectionEntry>

const PACKAGING_MINIMISATION_OUTCOME_SECTION_ID = '2.2'

const PACKAGING_MINIMISATION_CHECKLIST_ROWS: ReadonlyArray<{
  label: string
  criterionKey: keyof PackagingMinimisationCriteriaAgentResult | null
}> = [
  { label: 'Product protection', criterionKey: 'productProtection' },
  {
    label: 'Packaging manufacturing processes',
    criterionKey: 'packagingManufacturingProcess',
  },
  { label: 'Packing/filling process', criterionKey: 'packingFillingProcess' },
  {
    label: 'Logistics (inc. Transport, warehousing and handling)',
    criterionKey: 'logistics',
  },
  {
    label: 'Product presentation and marketing',
    criterionKey: 'productPresentationAndMarketing',
  },
  {
    label: 'User/consumer acceptance',
    criterionKey: 'userConsumerAcceptance',
  },
  { label: 'Information', criterionKey: 'informationPC' },
  { label: 'Safety', criterionKey: 'safetyPC' },
  { label: 'Legislation', criterionKey: 'legislationPC' },
  { label: 'Other issues', criterionKey: null },
]

function escapeMarkdownTableCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\n/g, ' ')
}

function buildStandardCriterionMarkdown(criterion: {
  isCriticalArea: boolean
  explanation: string
  followUpQuestion?: string | null
}): string {
  const criticalAreaLabel = criterion.isCriticalArea ? 'Yes' : 'No'
  const parts = [
    `**Critical area:** ${criticalAreaLabel}`,
    criterion.explanation.trim(),
  ]
  const followUpQuestion = criterion.followUpQuestion?.trim()
  if (followUpQuestion != null && followUpQuestion.length > 0) {
    parts.push(manualInstructionWithGuidance(followUpQuestion))
  }
  return parts.join('\n\n')
}

function buildLegislationCriterionMarkdown(criterion: {
  explanation: string
}): string {
  return criterion.explanation.trim()
}

function buildPackagingMinimisationCriterionMarkdown(
  entry: CriterionSectionEntry,
  agentResult: PackagingMinimisationCriteriaAgentResult
): string {
  if (entry.kind === 'legislation') {
    return buildLegislationCriterionMarkdown(agentResult.legislationPC)
  }
  return buildStandardCriterionMarkdown(agentResult[entry.criterionKey])
}

export function buildPackagingMinimisationOutcomeMarkdown(
  outcome: PackagingMinimisationOutcomeAgentShape
): string {
  const weight = outcome.totalMinimumNecessaryWeightGrams ?? '___'
  const volume = outcome.totalMinimumNecessaryVolumeCm3 ?? '___'
  const parts = [
    `- Total Minimum Necessary Weight: ${weight} grams`,
    `- Total Minimum Necessary Volume: ${volume} cm³`,
  ]

  const bothValuesMissing =
    outcome.totalMinimumNecessaryWeightGrams == null &&
    outcome.totalMinimumNecessaryVolumeCm3 == null

  if (bothValuesMissing) {
    parts.push('', INSUFFICIENT_INFORMATION_PLACEHOLDER)
  } else if (outcome.reasoning != null && outcome.reasoning.trim().length > 0) {
    parts.push('', outcome.reasoning.trim())
  }

  return parts.join('\n')
}

export function buildPackagingMinimisationChecklistMarkdown(
  agentResult: PackagingMinimisationCriteriaAgentResult
): string {
  const header = `| Performance Criterion | Most important/relevant requirement | Critical area | References |
| --- | --- | --- | --- |`

  const dataRows = PACKAGING_MINIMISATION_CHECKLIST_ROWS.map((row) => {
    if (row.criterionKey == null) {
      return `| ${row.label} | | | |`
    }

    const criterion = agentResult[row.criterionKey]
    if ('isCriticalArea' in criterion) {
      const criticalAreaLabel = criterion.isCriticalArea ? 'Yes' : 'No'
      const mostImportantRequirement =
        criterion.isCriticalArea &&
        criterion.mostImportantRequirement != null &&
        criterion.mostImportantRequirement.trim().length > 0
          ? escapeMarkdownTableCell(criterion.mostImportantRequirement.trim())
          : ''
      const requirementCell =
        mostImportantRequirement.length > 0
          ? ` ${mostImportantRequirement} `
          : ' '

      return `| ${row.label} |${requirementCell}| ${criticalAreaLabel} | |`
    }

    return `| ${row.label} | | | |`
  })

  return [header, ...dataRows].join('\n')
}

export function mergePackagingMinimisationAssessmentHistoryIntoSections(
  sections: PackagingMinimisationAssessmentSectionRecord[],
  agentResult: PackagingMinimisationCriteriaAgentResult,
  outcome: PackagingMinimisationOutcomeAgentShape
): PackagingMinimisationAssessmentSectionRecord[] {
  const markdownBySectionId = new Map<string, string>()

  markdownBySectionId.set(
    PACKAGING_MINIMISATION_OUTCOME_SECTION_ID,
    buildPackagingMinimisationOutcomeMarkdown(outcome)
  )

  for (const entry of PACKAGING_MINIMISATION_CRITERION_SECTIONS) {
    markdownBySectionId.set(
      entry.sectionId,
      buildPackagingMinimisationCriterionMarkdown(entry, agentResult)
    )
  }

  markdownBySectionId.set(
    '3',
    buildPackagingMinimisationChecklistMarkdown(agentResult)
  )

  return sections.map((section) => {
    const markdown = markdownBySectionId.get(section.sectionId)
    if (markdown == null) {
      return section
    }
    return { ...section, markdown }
  })
}
